import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Film, Trash2, Clock, Plus } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';

const STATUS = {
  uploaded: ['Uploaded', 'text-slate-300 bg-slate-500/15'],
  analyzing: ['Analyzing', 'text-amber-300 bg-amber-500/15'],
  analyzed: ['Analyzed', 'text-violet-300 bg-violet-500/15'],
  exported: ['Exported', 'text-emerald-300 bg-emerald-500/15'],
  failed: ['Failed', 'text-rose-300 bg-rose-500/15'],
};

export function ProjectCard({ p, onDelete }) {
  const nav = useNavigate();
  const [s, cls] = STATUS[p.status] || STATUS.uploaded;
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project?')) return;
    await api.delete(`/projects/${p.id}`);
    toast.success('Project deleted');
    onDelete?.(p.id);
  };
  return (
    <div data-testid={`project-card-${p.id}`} onClick={() => nav(`/app/studio/${p.id}`)}
      className="group rounded-2xl border border-white/10 bg-[#12141D] overflow-hidden cursor-pointer hover:border-[#FF4D00]/40 transition-colors">
      <div className="h-32 bg-gradient-to-br from-[#1a1d2a] to-[#0B0C12] grid place-items-center relative">
        <Film className="w-8 h-8 text-slate-600" />
        <span className={`absolute top-3 left-3 text-[11px] font-mono px-2 py-0.5 rounded-full ${cls}`}>{s}</span>
        {onDelete && (
          <button onClick={del} data-testid={`delete-${p.id}`} className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-full bg-black/40 text-slate-400 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="font-heading font-semibold truncate">{p.title || 'Untitled'}</div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-2 font-mono">
          <Clock className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const load = () => api.get('/projects').then(({ data }) => setProjects(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Projects</h1>
        <Button onClick={() => nav('/app/create')} className="rounded-full"><Plus className="w-4 h-4 mr-1" /> New Video</Button>
      </div>
      {projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/12 bg-[#0B0C12] p-12 text-center">
          <Film className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <div className="text-slate-300">No projects yet.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => <ProjectCard key={p.id} p={p} onDelete={(id) => setProjects((x) => x.filter((q) => q.id !== id))} />)}
        </div>
      )}
    </div>
  );
}
